<template>
  <div class="material-option-content">
    <span class="material-option-content__descripcion">
      <span
        v-for="parte in partesDescripcion"
        :key="parte.indice"
        :class="{ 'material-option-content__coincidencia': parte.coincide }"
        v-text="parte.texto"
      />
    </span>
    <span
      v-if="codigos.length"
      class="material-option-content__codigos"
    >
      <span
        v-for="codigo in codigos"
        :key="codigo.tipo"
        class="material-option-content__codigo"
      >
        <span
          class="material-option-content__badge"
          :title="codigo.titulo"
          v-text="codigo.badge"
        />
        <span>
          <span
            v-for="parte in codigo.partes"
            :key="parte.indice"
            :class="{ 'material-option-content__coincidencia': parte.coincide }"
            v-text="parte.texto"
          />
        </span>
      </span>
    </span>
  </div>
</template>

<script setup>
// Contenido de una opción de los selectores de material: descripción interna y, debajo y más
// chico, el código equivalente y el código adicional. Si recibe `busqueda`, resalta las
// coincidencias. Se usa en los <el-option> y en el slot #label (valor seleccionado) de
// `input_materiales.vue`; para volver al formato de una sola línea basta con dejar de usarlo ahí.
import { computed } from 'vue';

const props = defineProps({
  material: {
    type: Object,
    default: null,
  },
  busqueda: {
    type: String,
    default: '',
  },
});

// Minúsculas y sin tildes, carácter por carácter, para que cada posición del texto normalizado
// corresponda a la misma posición del original (el backend busca sin distinguir mayúsculas).
function normalizar(texto) {
  return Array.from(texto, (c) =>
    c.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase().charAt(0),
  ).join('');
}

function dividirPorCoincidencias(texto, busqueda) {
  const termino = normalizar(busqueda.trim());
  if (!texto || !termino) return texto ? [{ indice: 0, texto, coincide: false }] : [];

  const caracteres = Array.from(texto);
  const normalizado = normalizar(texto);
  const partes = [];
  let desde = 0;
  let indice = normalizado.indexOf(termino);
  while (indice !== -1) {
    if (indice > desde) {
      partes.push({
        indice: desde,
        texto: caracteres.slice(desde, indice).join(''),
        coincide: false,
      });
    }
    const hasta = indice + termino.length;
    partes.push({ indice, texto: caracteres.slice(indice, hasta).join(''), coincide: true });
    desde = hasta;
    indice = normalizado.indexOf(termino, desde);
  }
  if (desde < caracteres.length) {
    partes.push({ indice: desde, texto: caracteres.slice(desde).join(''), coincide: false });
  }
  return partes;
}

// Cada código lleva un badge que indica cuál es; sólo se listan los que el material tiene.
const TIPOS_CODIGO = [
  { tipo: 'CodigoEquivalente', badge: 'Equiv.', titulo: 'Código equivalente' },
  { tipo: 'CodigoAdicional', badge: 'Adic.', titulo: 'Código adicional' },
];

const partesDescripcion = computed(() =>
  dividirPorCoincidencias(props.material?.DescripcionInterna ?? '', props.busqueda),
);

const codigos = computed(() =>
  TIPOS_CODIGO.filter(({ tipo }) => props.material?.[tipo]).map((definicion) => ({
    ...definicion,
    partes: dividirPorCoincidencias(String(props.material[definicion.tipo]), props.busqueda),
  })),
);
</script>

<style>
/* Global a propósito: el dropdown de el-select se teletransporta fuera del componente y la
   opción tiene altura fija de una línea; con dos renglones tiene que crecer. */
.el-select-dropdown__item:has(.material-option-content) {
  height: auto;
  padding-top: 4px;
  padding-bottom: 4px;
  line-height: normal;
}

/* Valor seleccionado (slot #label). El texto elegido se dibuja en `.el-select__placeholder`, que
   Element Plus centra en vertical (absoluto, top 50%), igual que el <input> de búsqueda: con dos
   renglones, el cursor quedaba a mitad de camino entre ambos. Se alinean los dos arriba, de modo
   que el input coincida con el renglón de la descripción (que hereda el line-height del select:
   24px por defecto, 20px en `small`), y el renglón de códigos queda debajo. Las flechas e iconos
   del sufijo siguen centrados. El placeholder es absoluto y no empuja la altura: el min-height es
   padding vertical + descripción + renglón de códigos (16px: badge de 14px más su borde). */
.el-select__wrapper:has(.el-select__placeholder .material-option-content) {
  min-height: 48px;
}

.el-select--small .el-select__wrapper:has(.el-select__placeholder .material-option-content) {
  min-height: 40px;
}

.el-select__wrapper:has(.el-select__placeholder .material-option-content) .el-select__selection {
  align-self: stretch;
  align-items: flex-start;
}

.el-select__placeholder:has(.material-option-content) {
  top: 0;
  transform: none;
}

.el-select__placeholder .material-option-content__codigos {
  height: 16px;
  line-height: 16px;
}

.material-option-content {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.material-option-content__descripcion {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.material-option-content__codigos {
  overflow: hidden;
  color: var(--el-text-color-secondary);
  font-size: 11px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.material-option-content__codigo {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.material-option-content__codigo + .material-option-content__codigo {
  margin-left: 10px;
}

.material-option-content__badge {
  padding: 0 4px;
  border: 1px solid var(--el-border-color);
  border-radius: 3px;
  background-color: var(--el-fill-color-light);
  color: var(--el-text-color-secondary);
  font-size: 10px;
  font-weight: 600;
  line-height: 14px;
  text-transform: uppercase;
}

.material-option-content__coincidencia {
  padding: 0;
  border-radius: 2px;
  background-color: var(--el-color-warning-light-7);
  color: inherit;
  font-weight: 600;
}
</style>
